import { useSelector } from "react-redux";
import {
    getCountryDataBasedOnTimezone,
    getUserTimezone,
} from "repoV2/features/Common/modules/Timezone/Timezone.utils";
import { IRepoV2ReduxState } from "repoV2/features/Common/modules/Reducer/Reducer.interface";
import { INDIA_COUNTRY_DATA } from "repoV2/utils/common/location/countriesData";
import useGetFormData from "repoV2/features/Common/modules/FormData/utils/useGetFormData";
import useSubmitExpressInterest from "repoV2/features/ExpressInterest/utils/useSubmitExpressInterest";
import useSetIsExpressInterestProcessing from "repoV2/features/CheckoutPages/utils/useSetIsExpressInterestProcessing";
import { FORM_KEYS } from "repoV2/features/Common/modules/FormData/FormData.constants";
import { IExpressInterestFormValues } from "repoV2/features/Common/modules/FormData/interfaces/ExpressInterestFormWrapper.interfaces";
import {
    FORM_LOG_TYPES,
    LISTING_ENUM,
    LISTING_STATUSES,
} from "repoV2/features/Listing/Listing.constants";
import { postAnalytics } from "repoV2/features/Common/Analytics/Analytics.utils";
import { ANALYTICS_EVENTS } from "repoV2/features/Common/Analytics/Analytics.constants";
import { ILeadsQuestion } from "repoV2/features/CheckoutPages/utils/apis/CustomQuestionsApi/ICustomQuestionsApi.interface";
import {
    CUSTOM_QUESTION_TYPES_ID,
    other_dropdown_option,
} from "repoV2/features/CustomQuestions/constants/CustomQuestionsTypes.constants";
import { checkCustomQuestionAnswerValid } from "repoV2/features/CustomQuestions/utils/checkCustomQuestionAnswerValid";
import { getOtherOptionFieldNameWithSuffix } from "repoV2/features/CustomQuestions/utils/CustomQuestions.utils";
import useToastMessage from "repoV2/components/common/Toast/useToastMessage";
import { FieldValues } from "react-hook-form";
import { express_interest_form } from "../ExpressInterestForm/ExpressInterestForm.constants";
import { IExpressInterestFormPayloadValues } from "./ExpressInterestFormWrapper.interfaces";
import { useCheckoutBasicDetailsContext } from "../../../CheckoutBasicDetailsContextProvider";
import { useExpressInterestContext } from "../../../CheckoutExpressInterestContextProider";
import { checkout_form_error_strings } from "../../../PaymentDetailsSection/modules/CheckoutForm/CheckoutForm.constants";

const {
    basic_details: {
        field_names: { name, email, phone, country_code, message },
    },
} = express_interest_form;

const computeDefaultFormValuesFromRedux = (
    data: Partial<IExpressInterestFormValues> = {}
) => {
    const timezone = getUserTimezone();
    const { dial_code: defaultDialCode = INDIA_COUNTRY_DATA.dial_code } =
        getCountryDataBasedOnTimezone({ timezone }) || {};

    const defaultValues: IExpressInterestFormValues = {
        name: data[name] || "",
        email: data[email] || "",
        phone: data[phone] || "",
        country_code: data[country_code] || defaultDialCode,
        message: data[message] || "",
    };

    return defaultValues;
};

const convertFormValuesToSubmitExpressInterestPayload = (
    values: FieldValues,
    leadsQuestions: ILeadsQuestion[] | undefined
) => {
    const expressInterestCustomQuestions: IExpressInterestFormPayloadValues["expressInterestCustomQuestions"] =
        [];

    /**
     * an array of all the lead questions which have answer
     * Need this to evaluate the data to be sent on sessionStorage, to be used in Thank you Optin page
     */
    const customQuestions: ILeadsQuestion[] = [];
    /**
     * object with key as question uuid and its respective answer
     * Need this to evaluate the data to be sent on sessionStorage, to be used in Thank you Optin page
     */
    const customAnswers: { [key: string]: string } = {};

    if (leadsQuestions?.length) {
        leadsQuestions.forEach(question => {
            let answer = values[question.uuid] || "";
            if (question.type === CUSTOM_QUESTION_TYPES_ID.CALENDAR && answer) {
                answer = new Date(answer).toISOString();
            }
            // Incase of dropdown fields, if user selects other, we append "--other"
            // to get the actual value of the input
            if (
                question.type === CUSTOM_QUESTION_TYPES_ID.INDIAN_CITY ||
                question.type === CUSTOM_QUESTION_TYPES_ID.GENDER
            ) {
                answer =
                    answer === other_dropdown_option.value
                        ? values[
                              getOtherOptionFieldNameWithSuffix(question.uuid)
                          ]
                        : answer;
            }

            // Incase answer does not exist for a question, then we do not send it
            // in expressInterestCustomQuestions and to backend api
            if (!checkCustomQuestionAnswerValid(question.type, answer)) {
                return;
            }

            customQuestions.push(question);
            customAnswers[question.uuid] = answer;
            expressInterestCustomQuestions.push({
                ques: question.uuid,
                ans: answer,
                thumbnail: values[`${question.uuid}-file`]?.thumbnail,
            });
        });
    }

    const expressInterestFormPayloadValues: IExpressInterestFormPayloadValues =
        {
            name: values?.name,
            email: values?.email,
            phone: values?.phone,
            country_code: values?.country_code,
            message: values?.message,
            expressInterestCustomQuestions,
        };
    return { expressInterestFormPayloadValues, customQuestions, customAnswers };
};

export const useExpressInterestFormWrapper = () => {
    const { submitExpressInterest } = useSubmitExpressInterest();
    const { setIsExpressInterestProcessing } =
        useSetIsExpressInterestProcessing();
    const { notify, toast_type_keys } = useToastMessage();
    const data =
        useGetFormData<IExpressInterestFormValues>({
            key: `${FORM_KEYS.express_interest}`,
        }) || {};

    const {
        listingUuid,
        listingType,
        hostName,
        listingStatus,
        dynamicLinkUuid,
        title,
        isCheckoutPagePreviewMode,
    } = useCheckoutBasicDetailsContext();

    const priceDetails = useSelector(
        (mainState: IRepoV2ReduxState) =>
            mainState.checkoutPages.listingDetails[listingUuid]?.priceSection
                ?.data
    );

    const { currency_name, price_per_head } = priceDetails || {};

    const { postInterestUrl = "" } = useExpressInterestContext();

    const leadsQuestions = useSelector(
        (state: IRepoV2ReduxState) =>
            state.checkoutPages.listingDetails[listingUuid]?.customQuestions
                ?.data?.leads_questions
    );

    const defaultValues = computeDefaultFormValuesFromRedux(data);
    const isSoldOut = listingStatus === LISTING_STATUSES.SOLD_OUT;

    /**
     * on click handler of the submit button
     * @param values Form values of the express interest form
     */
    const handleSubmit = async ({ values }: { values: FieldValues }) => {
        if (isCheckoutPagePreviewMode) {
            return;
        }
        const {
            expressInterestFormPayloadValues: returnValues,
            customQuestions,
            customAnswers,
        } = convertFormValuesToSubmitExpressInterestPayload(
            values,
            leadsQuestions
        );

        const {
            email: customerEmail,
            name: customerName,
            phone: customerPhone,
            country_code: countryCode,
        } = returnValues ?? {};
        /**
         * analytics event for "Join Waitlist" or "Express Interest" form submit
         */
        postAnalytics(ANALYTICS_EVENTS.LEAD, {
            sub_domain: hostName,
            event_source_url: window.location.href,
            content_name: title,
            content_category: LISTING_ENUM[listingType].TITLE,
            currency: currency_name,
            value: price_per_head,
            user_data: {
                em: customerEmail,
                name: customerName,
                ph: `${countryCode}${customerPhone}`,
            },
        });

        try {
            setIsExpressInterestProcessing(true);
            await submitExpressInterest({
                expressInterestFormValues: returnValues,
                hostName,
                listingUuid,
                dynamicLinkUuid,
                isSoldOut,
                postInterestUrl,
                unprocessedLeadsQuestionAndAnswer: {
                    customQuestions: customQuestions || [],
                    customAnswers,
                },
                logType: FORM_LOG_TYPES.EXPRESSED_INTEREST,
            });
        } finally {
            setIsExpressInterestProcessing(false);
        }
    };

    const handleError = () => {
        notify(checkout_form_error_strings.submit_all, {
            type: toast_type_keys.error,
        });
    };

    return {
        defaultValues,
        handleSubmit,
        handleError,
    };
};
